import { Game, PlayerSession, GameCreateResponse, MoveResponse, GameListItem } from '@/types/game';

const DEFAULT_API_BASE_URL = 'https://reversi-api.gp4android7.workers.dev';
const API_BASE_URL = (import.meta.env.VITE_API_URL || DEFAULT_API_BASE_URL).replace(/\/$/, '');

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      
      // 開発環境用のフォールバック
      if (endpoint === '/api/games') {
        return {
          success: true,
          data: []
        } as any;
      }
      
      if (endpoint === '/api/session') {
        return {
          success: true,
          data: {
            sessionId: 'mock-session-' + Date.now(),
            name: 'TestPlayer'
          }
        } as any;
      }
      
      throw error;
    }
  }

  async createSession(name: string): Promise<{ success: boolean; data: PlayerSession; error?: string }> {
    return this.request<{ success: boolean; data: PlayerSession; error?: string }>('/api/session', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getGames(): Promise<{ success: boolean; data: GameListItem[]; error?: string }> {
    return this.request<{ success: boolean; data: GameListItem[]; error?: string }>('/api/games');
  }

  async createGame(sessionId: string, name: string): Promise<{ success: boolean; data: GameCreateResponse; error?: string }> {
    return this.request<{ success: boolean; data: GameCreateResponse; error?: string }>('/api/games', {
      method: 'POST',
      body: JSON.stringify({ sessionId, name }),
    });
  }

  async joinGame(gameId: string, sessionId: string): Promise<{ success: boolean; data: { message: string }; error?: string }> {
    return this.request<{ success: boolean; data: { message: string }; error?: string }>(`/api/games/${gameId}/join`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async getGame(gameId: string): Promise<{ success: boolean; data: Game; error?: string }> {
    return this.request<{ success: boolean; data: Game; error?: string }>(`/api/games/${gameId}`);
  }

  async makeMove(
    gameId: string,
    sessionId: string,
    row: number,
    col: number
  ): Promise<{ success: boolean; data: MoveResponse; error?: string }> {
    return this.request<{ success: boolean; data: MoveResponse; error?: string }>(`/api/games/${gameId}/move`, {
      method: 'POST',
      body: JSON.stringify({ sessionId, row, col }),
    });
  }

  async leaveGame(gameId: string, sessionId: string): Promise<{ success: boolean; data: { message: string }; error?: string }> {
    return this.request<{ success: boolean; data: { message: string }; error?: string }>(`/api/games/${gameId}/leave`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async deleteGame(gameId: string, sessionId: string): Promise<{ success: boolean; data: { message: string }; error?: string }> {
    return this.request<{ success: boolean; data: { message: string }; error?: string }>(`/api/games/${gameId}`, {
      method: 'DELETE',
      body: JSON.stringify({ sessionId }),
    });
  }
}

export const api = new ApiClient(API_BASE_URL); 
